import type { JobPosition, School } from '../types/User.ts'
import { htmlToPlainText } from './htmlText.ts'

/**
 * Job recommendations on /praca ("Odporúčané") and the text the jobs search index is built from.
 *
 * Ranking = semantic similarity of the job to the person's profile (Typesense vector distance)
 * plus small adjustments for what the profile says explicitly: seniority, preferred job type,
 * place and form of collaboration, and how fresh the job is. The weights were set by
 * scripts/jobs/eval-job-recommendations.ts on real applications; re-run it before changing them.
 */

// Ordered from the least to the most experienced, matching WhoType values on jobs.
const SENIORITY_ORDER = ['student_ss', 'student_vs', 'cerstvy_absolvent_vs', 'mlady_profesional', 'profesional'] as const
export type JobSeniority = (typeof SENIORITY_ORDER)[number]

const MAX_QUERY_LENGTH = 1000
const MAX_RECENT_POSITIONS = 4

/** Text of a job for the search index: what the work is and whom they look for, without HTML. */
export function getJobSearchDescription(job: {
    description?: string
    content?: string
    challenge?: string
    desc?: string
}): string {
    const parts = [htmlToPlainText(job.challenge), htmlToPlainText(job.desc)].filter(Boolean)
    if (parts.length > 0) return parts.join(' ')
    // Imported and older jobs keep the whole text in one field
    return htmlToPlainText(job.description) || htmlToPlainText(job.content)
}

export type JobRecommendationProfile = {
    interests?: string[]
    jobs?: JobPosition[]
    universities?: School[]
    mentorProfile?: { interests?: string[] }
    talentBaseUserProfile?: {
        keywords?: string[]
        why?: string
        jobPreferences?: {
            jobType?: string[]
            formOfCollaboration?: string[]
            jobPlace?: string[]
            jobLocation?: string[]
        }
    }
}

function uniqueStrings(values: unknown[]): string[] {
    const seen = new Set<string>()
    const result: string[] = []
    for (const value of values) {
        if (typeof value !== 'string') continue
        const trimmed = value.trim()
        const key = trimmed.toLowerCase()
        if (!trimmed || seen.has(key)) continue
        seen.add(key)
        result.push(trimmed)
    }
    return result
}

function startYearOf(job: JobPosition): number {
    return typeof job.startYear === 'number' ? job.startYear : 0
}

function recentPositions(profile: JobRecommendationProfile): string[] {
    const positions = [...(profile.jobs ?? [])].sort((a, b) => startYearOf(b) - startYearOf(a)).map((job) => job.position)
    return uniqueStrings(positions).slice(0, MAX_RECENT_POSITIONS)
}

function cutOnWord(text: string, maxLength: number): string {
    if (text.length <= maxLength) return text
    const cut = text.slice(0, maxLength)
    const lastSpace = cut.lastIndexOf(' ')
    return lastSpace > 0 ? cut.slice(0, lastSpace) : cut
}

/**
 * What the person does and is interested in, in a few short phrases. Employer names, language
 * codes and long descriptions are left out: they pull the embedding away from the kind of work.
 * So is "Čo hľadám" (TalentBase `why`): it is mostly about motivation, and the evaluation ranked
 * applied jobs slightly better without it.
 */
export function buildJobRecommendationQuery(profile: JobRecommendationProfile | null | undefined): string {
    if (!profile) return ''
    const tb = profile.talentBaseUserProfile
    const keywords = uniqueStrings([...(tb?.keywords ?? []), ...(profile.interests ?? []), ...(profile.mentorProfile?.interests ?? [])])
    const positions = recentPositions(profile)
    const fields = uniqueStrings((profile.universities ?? []).map((school) => school.field || school.specialistation))

    const text = [
        keywords.join(', '),
        positions.join(', '),
        fields.join(', ')
    ]
        .filter(Boolean)
        .join('. ')

    return cutOnWord(text, MAX_QUERY_LENGTH)
}

function toYear(value: unknown): number | undefined {
    if (typeof value === 'number' && value > 1900) return value
    if (value instanceof Date) return value.getFullYear()
    if (typeof value === 'string') {
        const year = new Date(value).getFullYear()
        return Number.isNaN(year) ? undefined : year
    }
    if (value && typeof value === 'object') {
        const record = value as { toDate?: () => Date; seconds?: number; _seconds?: number }
        if (typeof record.toDate === 'function') return record.toDate().getFullYear()
        const seconds = record.seconds ?? record._seconds
        if (typeof seconds === 'number') return new Date(seconds * 1000).getFullYear()
    }
    return undefined
}

/**
 * Seniority from the work history and education in the profile, in the categories jobs are
 * posted for. Undefined when the profile does not say enough, and then seniority is not used.
 */
export function getProfileSeniority(profile: JobRecommendationProfile, now: number = Date.now()): JobSeniority | undefined {
    const currentYear = new Date(now).getFullYear()

    const workStartYears = (profile.jobs ?? []).map((job) => toYear(job.startYear)).filter((year): year is number => !!year && year <= currentYear)
    const workYears = workStartYears.length > 0 ? currentYear - Math.min(...workStartYears) : 0

    const schools = profile.universities ?? []
    const isStudying = schools.some((school) => {
        const end = toYear(school.endYear) ?? toYear(school.endDate)
        if (end) return end >= currentYear
        const start = toYear(school.startYear) ?? toYear(school.startDate)
        return !!start && start >= currentYear - 5
    })
    const graduationYears = schools
        .map((school) => toYear(school.endYear) ?? toYear(school.endDate))
        .filter((year): year is number => !!year && year < currentYear)
    const lastGraduation = graduationYears.length > 0 ? Math.max(...graduationYears) : undefined

    // Only clear cases: a wrong guess hides jobs the person would apply to. Student part-time jobs
    // and secondary schools entered as universities made the looser rules call students professionals.
    const positions = workStartYears.length
    if (workYears >= 8 && positions >= 2) return 'profesional'
    if (isStudying) return 'student_vs'
    if (lastGraduation !== undefined && currentYear - lastGraduation <= 1) return 'cerstvy_absolvent_vs'
    if (lastGraduation !== undefined && currentYear - lastGraduation <= 3) return 'mlady_profesional'
    if (workYears >= 5 && positions >= 2) return 'profesional'
    return undefined
}

export type JobRecommendationSignals = {
    seniority?: JobSeniority
    jobType: string[]
    formOfCollaboration: string[]
    jobPlace: string[]
    jobLocation: string[]
}

export function getJobRecommendationSignals(
    profile: JobRecommendationProfile | null | undefined,
    now: number = Date.now()
): JobRecommendationSignals {
    const preferences = profile?.talentBaseUserProfile?.jobPreferences
    return {
        seniority: profile ? getProfileSeniority(profile, now) : undefined,
        jobType: uniqueStrings(preferences?.jobType ?? []),
        formOfCollaboration: uniqueStrings(preferences?.formOfCollaboration ?? []),
        jobPlace: uniqueStrings(preferences?.jobPlace ?? []),
        jobLocation: uniqueStrings(preferences?.jobLocation ?? [])
    }
}

export type RecommendableJob = {
    title?: string
    who?: string[]
    type?: string[] | string
    jobPlace?: string[] | string
    jobLocation?: string[]
    formOfCollaboration?: string[]
    publishedAt?: number
    createdAt?: number
}

export type JobRecommendationWeights = {
    /** Penalty when the job is for the neighbouring seniority level only. */
    seniorityNear: number
    /** Penalty when the job is only for people two or more levels above. */
    seniorityFar: number
    /**
     * Penalty when the job is only for people two or more levels below, or is a student internship
     * for someone already working. 1 puts such jobs after all others: a senior would rather see a
     * role that is a stretch than an internship.
     */
    belowLevel: number
    /**
     * Penalty for purely manual work that needs no expertise (cleaning, warehouse). 2 puts it
     * after everything, below-level jobs included, for everyone (isManualWorkJob).
     */
    manualWork: number
    /**
     * Penalty for each level the job reaches below the person beyond the neighbouring one. Orgs
     * often tick every audience, so "open to students too" is the real signal of a junior job.
     */
    openToJuniors: number
    /** Bonus for each matching preference (job type, form of collaboration, place). */
    preference: number
    /** Penalty when the job is on-site only in a city the person did not choose. */
    locationMismatch: number
    /** Bonus for a just-published job, fading out over freshnessDays. */
    freshness: number
    freshnessDays: number
}

export const DEFAULT_JOB_RECOMMENDATION_WEIGHTS: JobRecommendationWeights = {
    seniorityNear: 0.02,
    seniorityFar: 0.08,
    belowLevel: 1,
    manualWork: 2,
    openToJuniors: 0.03,
    preference: 0.02,
    locationMismatch: 0.03,
    freshness: 0.06,
    freshnessDays: 30
}

export type JobRecommendationReason = 'seniority' | 'jobType' | 'formOfCollaboration' | 'place' | 'new'

function asArray(value: string[] | string | undefined): string[] {
    if (Array.isArray(value)) return value
    return value ? [value] : []
}

function intersects(a: string[], b: string[]): boolean {
    const lower = new Set(a.map((value) => value.toLowerCase()))
    return b.some((value) => lower.has(value.toLowerCase()))
}

const STUDENT_INTERNSHIP = 'staz_pre_studenta'

/*
 * Purely manual work without expertise, by the title: jobs have no field for it, and orgs tick
 * every audience on these. Checked on all 4,510 jobs ever posted (2026-11): it leaves out
 * "manuálne testovanie", waiters and receptionists (service work, not manual).
 */
const MANUAL_WORK_TITLE = /upratov|čistiac|čistenie (priestor|kancel)|\bcleaner|\bcleaning|sklad|warehouse|baliar|vykládk|nakládk|manuálnu prácu|manuálna práca|pomocn(ý|á|é) (robotník|prác|sil)|záhradkárske práce/i

export function isManualWorkJob(job: { title?: string }): boolean {
    return MANUAL_WORK_TITLE.test(job.title ?? '')
}

/** Seniority adjustment of the score, and whether the job is for the person's own level. */
function seniorityAdjustment(
    seniority: JobSeniority,
    who: string[],
    jobTypes: string[],
    weights: JobRecommendationWeights
): { penalty: number; matches: boolean } {
    const own = SENIORITY_ORDER.indexOf(seniority)
    const levels = who.map((value) => SENIORITY_ORDER.indexOf(value as JobSeniority)).filter((index) => index >= 0)
    if (levels.length === 0) return { penalty: 0, matches: false }

    const lowest = Math.min(...levels)
    const highest = Math.max(...levels)
    const isStudentInternship = jobTypes.length > 0 && jobTypes.every((type) => type === STUDENT_INTERNSHIP)
    const isWorking = own >= SENIORITY_ORDER.indexOf('mlady_profesional')

    if (own - highest >= 2 || (isWorking && isStudentInternship)) return { penalty: weights.belowLevel, matches: false }

    const juniorPenalty = Math.max(0, own - lowest - 1) * weights.openToJuniors
    if (levels.includes(own)) return { penalty: juniorPenalty, matches: true }

    const distance = Math.min(...levels.map((level) => Math.abs(level - own)))
    return { penalty: juniorPenalty + (distance === 1 ? weights.seniorityNear : weights.seniorityFar), matches: false }
}

/**
 * Score of one job for one person. Similarity is 1 − cosine distance (≈ 0.4–0.7 in practice),
 * the adjustments are deliberately small next to it so the content of the job stays decisive.
 */
export function scoreJobRecommendation(
    job: RecommendableJob,
    vectorDistance: number | undefined,
    signals: JobRecommendationSignals,
    now: number = Date.now(),
    weights: JobRecommendationWeights = DEFAULT_JOB_RECOMMENDATION_WEIGHTS
): { score: number; reasons: JobRecommendationReason[] } {
    let score = typeof vectorDistance === 'number' ? 1 - vectorDistance : 0
    const reasons: JobRecommendationReason[] = []

    const jobTypes = asArray(job.type)

    if (isManualWorkJob(job)) score -= weights.manualWork

    if (signals.seniority) {
        const { penalty, matches } = seniorityAdjustment(signals.seniority, job.who ?? [], jobTypes, weights)
        score -= penalty
        if (matches) reasons.push('seniority')
    }

    if (signals.jobType.length > 0 && jobTypes.length > 0 && intersects(signals.jobType, jobTypes)) {
        score += weights.preference
        reasons.push('jobType')
    }

    const forms = job.formOfCollaboration ?? []
    if (signals.formOfCollaboration.length > 0 && forms.length > 0 && intersects(signals.formOfCollaboration, forms)) {
        score += weights.preference
        reasons.push('formOfCollaboration')
    }

    const places = asArray(job.jobPlace)
    const isRemote = places.includes('homeoffice') || places.includes('hybrid')
    const wantsRemote = signals.jobPlace.includes('homeoffice')
    if (signals.jobLocation.length > 0 || wantsRemote) {
        const cityMatches = intersects(signals.jobLocation, job.jobLocation ?? [])
        if (cityMatches || (wantsRemote && isRemote)) {
            score += weights.preference
            reasons.push('place')
        } else if (!isRemote && (job.jobLocation ?? []).length > 0) {
            score -= weights.locationMismatch
        }
    }

    const publishedAt = job.publishedAt ?? job.createdAt
    if (publishedAt && weights.freshnessDays > 0) {
        const ageDays = (now - publishedAt) / 86_400_000
        if (ageDays >= 0 && ageDays < weights.freshnessDays) {
            score += weights.freshness * (1 - ageDays / weights.freshnessDays)
            reasons.push('new')
        }
    }

    return { score, reasons }
}

/** Sorts jobs (best first) and attaches the score and reasons to each. */
export function rankJobRecommendations<T extends RecommendableJob>(
    jobs: T[],
    getVectorDistance: (job: T) => number | undefined,
    signals: JobRecommendationSignals,
    now: number = Date.now(),
    weights: JobRecommendationWeights = DEFAULT_JOB_RECOMMENDATION_WEIGHTS
): Array<T & { _recommendation: { score: number; reasons: JobRecommendationReason[] } }> {
    return jobs
        .map((job) => ({ ...job, _recommendation: scoreJobRecommendation(job, getVectorDistance(job), signals, now, weights) }))
        .sort((a, b) => b._recommendation.score - a._recommendation.score)
}
